/**
 * Maps request hostname → which experience to render at `/` (single Pages app, multi-host deploys).
 * Fail-open: unknown hosts → same landing as xo.watch (hub).
 */

export type RootLandingExperience = "xowatch_home" | "constellation" | "pitch" | "secure_execution" | "casino_continuum";

const XO_WATCH_HOSTS = new Set(["xo.watch", "www.xo.watch"]);
const CONSTELLATION_HOSTS = new Set(["constellation.bond", "www.constellation.bond", "xo.constellation.bond"]);
const AETHER_CV_HOSTS = new Set(["aether.cv", "www.aether.cv"]);
const SEC_CASH_HOSTS = new Set(["sec.cash", "www.sec.cash"]);
const XO_CASINO_HOSTS = new Set(["xo.casino", "www.xo.casino"]);

/** Lowercase host label without port (e.g. `xo.watch` from `xo.watch:5173`). */
export function normalizeHostname(hostname: string): string {
  const trimmed = hostname.trim().toLowerCase();
  if (!trimmed) return "";
  return trimmed.includes(":") ? (trimmed.split(":")[0] ?? "") : trimmed;
}

export function resolveRootLandingExperience(hostname: string): RootLandingExperience {
  const h = normalizeHostname(hostname);
  if (AETHER_CV_HOSTS.has(h)) return "pitch";
  if (SEC_CASH_HOSTS.has(h)) return "secure_execution";
  if (XO_CASINO_HOSTS.has(h)) return "casino_continuum";
  if (CONSTELLATION_HOSTS.has(h)) return "constellation";
  if (XO_WATCH_HOSTS.has(h)) return "xowatch_home";
  return "xowatch_home";
}

/** Hosts where `/registrar` is an intentional operator surface (not static pitch/secure-only fronts). */
export function isRegistrarAllowedHost(hostname: string): boolean {
  const h = normalizeHostname(hostname);
  if (XO_WATCH_HOSTS.has(h)) return true;
  if (h === "localhost" || h === "127.0.0.1") return true;
  return false;
}

/** sec.cash, xo.watch, aether.cv (+ www) — static Pages fronts that must not assume same-origin Agent `/api`. */
export function isPublicStaticAetherFrontHost(hostname: string): boolean {
  const h = normalizeHostname(hostname);
  return SEC_CASH_HOSTS.has(h) || XO_CASINO_HOSTS.has(h) || XO_WATCH_HOSTS.has(h) || CONSTELLATION_HOSTS.has(h) || AETHER_CV_HOSTS.has(h);
}

/** Quiet redirect when registrar is not offered on this host. */
export function registrarRedirectForHost(hostname: string): string {
  const h = normalizeHostname(hostname);
  if (AETHER_CV_HOSTS.has(h)) return "/pitch";
  return "/";
}
